const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  API_KEY: process.env.REACT_APP_API_KEY || '',
  HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
};

// Token refresh management
let tokenRefreshPromise = null;
let lastRefreshTime = 0;
const REFRESH_INTERVAL = 14 * 60 * 1000; // 14 minutes (tokens typically expire in 15 minutes)

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.headers = {
      ...API_CONFIG.HEADERS,
      'X-API-Key': API_CONFIG.API_KEY
    };
  }

  async refreshTokenIfNeeded() {
    const now = Date.now();
    // If we refreshed recently, skip
    if (now - lastRefreshTime < REFRESH_INTERVAL) {
      return;
    }
    
    // If a refresh is already in progress, wait for it
    if (tokenRefreshPromise) {
      return tokenRefreshPromise;
    }

    // Start a new refresh
    tokenRefreshPromise = (async () => {
      try {
        const res = await fetch(`${this.baseURL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...this.headers },
          body: JSON.stringify({}),
        });
        
        if (res.ok) {
          lastRefreshTime = Date.now();
          console.log('Token refreshed successfully');
        } else if (res.status === 401) {
          // Refresh token expired, user needs to re-authenticate
          this.handleAuthError(401);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      } finally {
        tokenRefreshPromise = null;
      }
    })();

    return tokenRefreshPromise;
  }

  handleAuthError(status) {
    // Clear local storage and redirect to home/login
    const keysToKeep = ['reflection_', 'reflectionCompletedSets_', 'summary_', 'summaryRating_', 'congratsMessage_', 'reflectionText_'];
    Object.keys(localStorage).forEach(key => {
      if (!keysToKeep.some(prefix => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    });
    sessionStorage.clear();

    if (status === 401) {
      console.warn('Authentication required. Redirecting to login...');
      window.location.href = '/home';
    } else if (status === 403) {
      console.warn('Access forbidden. Insufficient permissions.');
      window.location.href = '/permission-error';
    }
  }

  async request(endpoint, options = {}) {
    // Try to refresh token before making request if needed
    await this.refreshTokenIfNeeded().catch(() => {
      // If refresh fails, continue with request - backend will return 401 if needed
    });

    const url = `${this.baseURL}${endpoint}`;
    
    // Don't set Content-Type header for FormData - let browser set it with boundary
    const headers = { ...this.headers };
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }
    
    const config = {
      headers,
      credentials: 'include', // include cookies for session management
      ...options
    };

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('content-type');
      
      // Handle authentication/authorization errors
      if (response.status === 401 || response.status === 403) {
        this.handleAuthError(response.status);
        const error = new Error(
          response.status === 401 
            ? 'Authentication required. Please log in.' 
            : 'Access forbidden. You do not have permission to access this resource.'
        );
        error.status = response.status;
        throw error;
      }
      
      if (!response.ok) {
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          // FastAPI validation errors can be arrays or single strings
          let errorMessage = '';
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => {
              if (typeof err === 'object' && err.loc && err.msg) {
                return `${err.loc.join('.')}: ${err.msg}`;
              }
              return String(err);
            }).join(', ');
          } else {
            errorMessage = errorData.detail || `HTTP error! Status: ${response.status}`;
          }
          const error = new Error(errorMessage);
          error.detail = errorData.detail;
          error.status = response.status;
          throw error;
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      }
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return await response.text();
    } catch (error) {
      throw error;
    }
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // Same authentication as request(), for endpoints that answer with a file
  // rather than JSON; request() would decode the bytes as text.
  async getBlob(endpoint) {
    await this.refreshTokenIfNeeded().catch(() => {});

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: this.headers,
      credentials: 'include',
    });

    if (response.status === 401 || response.status === 403) {
      this.handleAuthError(response.status);
    }
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.blob();
  }

  async post(endpoint, data) {
    // If data is FormData, don't stringify it - send as-is
    // Otherwise, stringify JSON data
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return this.request(endpoint, {
      method: 'POST',
      body: body
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

class ProblemSetService extends ApiService {
  async getProblemSetByTopic(topic, courseId) {
    const courseQuery = courseId == null ? "" : `?course=${encodeURIComponent(courseId)}`;
    return this.get(`/problem-set/by-topic/${encodeURIComponent(topic)}${courseQuery}`);
  }

  async deleteProblemSetByTopic(topic, courseId) {
    const courseQuery = courseId == null ? "" : `?course=${encodeURIComponent(courseId)}`;
    return this.delete(`/problem-set/delete-problem-set-by-topic/${encodeURIComponent(topic)}${courseQuery}`);
  }
}

// Question/Problem Service (aligned with backend API)
class QuestionService extends ApiService {
  // Backend-aligned endpoints
  async getProblem(problemId) {
    return this.get(`/problems/${problemId}`);
  }

  // One of a SQL question's expected-result screenshots, as the image itself.
  async getExpectedResultImage(problemId, imageIndex) {
    return this.getBlob(`/problems/${problemId}/expected-result-image/${imageIndex}`);
  }

  async getProblemsFromCourse(courseId) {
    return this.get(`/problems/from-course/${courseId}`);
  }

  async getProblemIds() {
    return this.get('/problems/problem-ids');
  }

  async getCompletedQuestionsByIdAndCourse(studentId, courseId) {
    return this.get(`/problems/completed-questions/by-id-and-course/${studentId}/${courseId}`);
  }

  async getIncompleteQuestionsByIdAndCourse(studentId, courseId) {
    return this.get(`/problems/incomplete-questions/by-id-and-course/${studentId}/${courseId}`);
  }

  async getTopics() {
    return this.get('/problems/topics');
  }
  async getAllTopicsAndProblemsByCourse(courseId) {
    return this.get(`/problems/all-by-course/${courseId}`);
  }
  async getCompletedQuestionsByStudentId(studentId) {
    return this.get(`/problems/completed-questions/by-id/${studentId}`);
  }

  async getIncompleteQuestionsByStudentId(studentId) {
    return this.get(`/problems/incomplete-questions/by-id/${studentId}`);
  }

  async createProblem(problemData) {
    return this.post('/problems/', problemData);
  }

  async updateProblem(problemId, problemData) {
    return this.put(`/problems/${problemId}`, problemData);
  }

  async deleteProblem(problemId) {
    return this.delete(`/problems/${problemId}`);
  }

  async storeProblem(problemData) {
    return this.post('/problems/store-problem', problemData);
  }

  async updateProblemById(questionId, problemData) {
    return this.patch(`/problems/update-problem/${questionId}`, problemData);
  }

  async deleteProblemById(questionId) {
    return this.delete(`/problems/delete-problem/${questionId}`);
  }

  async getCourseFromProblem(problemId) {
    return this.get(`/problems/course/${problemId}`);
  }

  // Legacy compatibility methods
  async getQuestions() {
    return this.getProblemIds();
  }

  async getQuestion(id) {
    return this.getProblem(id);
  }

  async deleteQuestion(id) {
    return this.deleteProblem(id);
  }

  async uploadAutograder(file, markdownFile, uploadTopicName, courseId) {
    const formData = new FormData();
    formData.append('zip_file', file);
    // Only append instructions_file if a file is actually provided
    // FastAPI File(None) means it's optional, so we can omit it entirely
    if (markdownFile) {
      formData.append('instructions_file', markdownFile);
    }
    
    formData.append('topicName', uploadTopicName);
    formData.append('courseId', courseId);
    // Debug: log what we're sending
    console.log('Uploading with:', {
      zip_file: file?.name,
      instructions_file: markdownFile?.name || 'not provided',
      topicName: uploadTopicName,
      courseId: courseId
    });
    
    return this.post('/problems/upload-autograder', formData);
  }

  async uploadSqlExam({
    zipFile,
    sourceFile,
    sourceField = 'student_file',
    instructionsFile = null,
    initFile = null,
    uploadTopicName,
    courseId,
  }) {
    const formData = new FormData();

    // Optional: without one, the backend builds the ZIP from the init file and template.
    if (zipFile) {
      formData.append('zip_file', zipFile);
    }
    formData.append(sourceField, sourceFile);

    if (instructionsFile) {
      formData.append('instructions_file', instructionsFile);
    }

    if (initFile) {
      formData.append('init_file', initFile);
    }

    formData.append('topicName', uploadTopicName);
    formData.append('courseId', courseId);

    console.log('Uploading SQL exam with:', {
      zip_file: zipFile?.name,
      [sourceField]: sourceFile?.name,
      instructions_file: instructionsFile?.name || 'not provided',
      init_file: initFile?.name || 'not provided',
      topicName: uploadTopicName,
      courseId,
    });

    return this.post('/problems/upload-sql-exam', formData);
  }

  async getAutograderStatus(uploadId) {
    return this.get(`/problems/autograder-status/${encodeURIComponent(uploadId)}`);
  }

  async getCompletedProblemSets(studentId, courseId) {
    return this.get(`/problems/completed-problem-sets/${studentId}/${courseId}`);
  }
}

// Submission Service (aligned with backend)
class SubmissionService extends ApiService {
  async runAutograder(submissionData) {
    return this.post('/submissions/run-autograder', submissionData);
  }

  async runSqlAutograder(submissionData) {
    return this.post('/submissions/run-sql-autograder', submissionData);
  }

  async runSubmission(submissionData) {
    return this.post('/submissions/run-submission', submissionData);
  }

  async storeSubmission(studentId, submissionData) {
    return this.post(`/submissions/store-submission/by-id/${studentId}`, submissionData);
  }

  async getSubmissionsByQuestion(questionId, studentId) {
    return this.get(`/submissions/by-question/${questionId}/${studentId}`);
  }

  async getSubmissionsFromQuestion(questionId) {
    return this.get(`/submissions/all-by-question/${questionId}`);
  }

  async getSubmissionIds() {
    return this.get('/submissions/submission-ids');
  }

  async getMostRecentSubmissions(studentId) {
    return this.get(`/submissions/by-student-id/${studentId}`);
  }

  async getAllSubmissionsByCourse(courseId) {
    return this.get(`/submissions/all-by-course/${courseId}`)
  }

  async getSubmissionsByStudentId(studentId) {
    return this.get(`/submissions/by-student-id/${studentId}`);
  }

  async getMostRecentSubmissionByStudentId(studentId) {
    return this.get(`/submissions/most-recent/by-student-id/${studentId}`);
  }
}

// User Service (aligned with backend)
class UserService extends ApiService {
  async getUsernames() {
    return this.get('/users/usernames');
  }

  async createStudent() {
    return this.post('/users/create-student');
  }

  async getStudentIdFromUsername(username) {
    return this.get(`/users/student-id/${username}`);
  }

  async getStudentByUsername(username) {
    return this.get(`/users/student/${username}`);
  }

  async getUser(username) {
    return this.get(`/users/get-user/${username}`);
  }
  async loginStudent(credentials) {
    return this.post('/users/login-with-gtid-and-password', credentials);
  }

  async registerStudent(credentials) {
    return this.post('/users/register-student-with-gtid-and-password', credentials);
  }
  
  async getCourse(studentId) {
    return this.get(`/users/get_course/${studentId}`)
  }

  async setConsentGiven(gtid) {
    return this.post(`/users/set-consent-given/${gtid}`);
  }

  async getConsentGiven(gtid) {
    return this.get(`/users/get-consent-given/${gtid}`);
  }

  async getChatConsentGiven(gtid) {
    return this.get(`/users/get-chat-consent-given/${gtid}`);
  }

  async setChatConsentGiven(gtid) {
    return this.post(`/users/set-chat-consent-given/${gtid}`);
  }
}

// LLM/Hint Service (aligned with backend) 
class LLMService extends ApiService {
  async generateHint(studentId, submissionId) {
    return this.get(`/llm/generate-hint-from-submission/direct/${studentId}/${submissionId}`);
  }

  async generateHintFromCode(requestData) {
    return this.post('/llm/generate-hint-from-code', requestData);
  }

  async getHintConceptSummary(studentId, courseId) {
    return this.get(`/llm/hint-concept-summary/${studentId}/${courseId}`);
  }

  async chat(studentId, courseId, messages, reflectionText, summaryRating, sessionId, conceptSummary) {
    return this.post(`/llm/chat/${studentId}/${courseId}`, {
      messages,
      reflection_text: reflectionText,
      summary_rating: summaryRating,
      session_id: sessionId,
      concept_summary: conceptSummary
    });
  }
}

class HintService extends ApiService {
  async getLatestHintIteration(studentId, questionId) {
    return this.get(`/hints/latest-hint-iteration/${studentId}/${questionId}`);
  }

  async getTotalHintsByStudent(studentId) {
    return this.get(`/hints/total-by-student/${studentId}`);
  }

  async getHintFeedback(studentId, questionId) {
    return this.get(`/hints/feedback/${studentId}/${questionId}`);
  }

  async submitHintFeedback(studentId, questionId, feedback) {
    return this.post(`/hints/feedback/${studentId}/${questionId}`, { feedback });
  }

  async getTotalHintsByStudentAndCourse(studentId, courseId) {
    return this.get(`/hints/total-by-student-and-course/${studentId}/${courseId}`);
  }

  async getAllHintsByStudentAndCourse(studentId, courseId) {
    return this.get(`/hints/all-by-student-and-course/${studentId}/${courseId}`);
  }

  async getMedianTimeBetweenHints(studentId, courseId) {
    return this.get(`/hints/median-time-between-hints/${studentId}/${courseId}`);
  }
}

class KnowledgeTracingService extends ApiService {
  async getAllKcsForUser(studentId) {
    return this.get(`/knowledge-tracing/get-all-kcs-for-user/${studentId}`);
  }

  async getKcForUserSubmission(studentId, submissionId) {
    return this.get(`/knowledge-tracing/get-kc-for-user-submission/${studentId}/${submissionId}`);
  }
}

class CourseService extends ApiService {
  async getCoursesForUsername(username) {
    return this.get(`/courses/get-courses-for-username/${username}`);
  }

  async getCoursesInProgress() {
    return this.get('/courses/get-courses-in-progress/');
  }

  async addStudentToCourse(studentId, crn) {
    return this.get(`/users/add-gtid-to-course/${studentId}/${crn}`);
  }

  async getCourse(courseId) {
    return this.get(`/courses/get-course/${courseId}`);
  }

  async joinCourse({ code }) {
    // Updated: Removed username - backend will extract user from JWT token
    // Send only the join code - user identity comes from authentication
    return this.post('/courses/join', { code });
  }

  async joinByCode({ code, gtid, role }) {
    // Backend expects { code, gtid, role? } — role is optional: "instructor", "ta", or "student"
    const body = { code, gtid };
    if (role) body.role = role;
    return this.post('/courses/join-by-code', body);
  }
}

class AnalyticsService extends ApiService {
  async getQuestionStatistics(questionId) {
    return this.get(`/analytics/question-statistics/${questionId}`);
  }

  async clusterErrors(questionId) {
    return this.get(`/analytics/cluster_errors/${questionId}`);
  }

  async errorsPerStudent(studentId) {
    return this.get(`/analytics/errors-per-student?student_id=${studentId}`);
  }

  async studentIndividualStatistics(studentId) {
    return this.get(`/analytics/student-individual-statistics/${studentId}`);
  }

  async getCourseId(courseName) {
    return this.get(`/analytics/course-id?coursename=${encodeURIComponent(courseName)}`);
  }

  async getCourses() {
    return this.get(`/analytics/get-course-names`);
  }

  async successRatePerTopic() {
    return this.get(`/analytics/success-rate-per-topic`);
  }

  async errorsPerCourse(courseId) {
    return this.get(`/analytics/errors-per-course?course_id=${courseId}`)
  }
  async logEvent(studentId, courseId, eventType, details = '') {
    try {
      return this.post('/analytics/log', { 
        studentID: studentId, 
        courseId: courseId,
        event_type: eventType, 
        details 
      });
    } catch (e) {}
  }
}

class ErrorsService extends ApiService {
  async getErrorsFromQuestion(questionId) {
    return this.get(`/errors/all-by-question/${questionId}`);
  }
  async getAllErrorsForStudent(studentId) {
    return this.get(`/errors/all-errors-for-student/${studentId}`);
  }
  async getErrorsWithCodeForStudent(studentId, topic, ccrn) {
    return this.get(`/errors/errors-with-code-for-student/${studentId}/${encodeURIComponent(topic)}?ccrn=${ccrn}`);
  }
  async getAllErrorsForStudent(studentId, ccrn = null) {
    const url = ccrn 
      ? `/errors/all-errors-for-student/${studentId}?ccrn=${ccrn}`
      : `/errors/all-errors-for-student/${studentId}`;
    return this.get(url);
  }
}

// ...existing code...
class AuthService {
  constructor(baseURL = "") {
    this.baseURL = baseURL || API_CONFIG.BASE_URL;
    this.headers = { ...API_CONFIG.HEADERS };
  }

  // Request backend to start OIDC. Handle JSON or 302 redirect responses.
  async getAuthorizationUrl(redirectPath) {
    const qp = redirectPath ? `?redirect=${encodeURIComponent(redirectPath)}` : "";
    const url = `${this.baseURL}/auth/login${qp}`;
    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      redirect: "manual", // don't follow IdP redirect so we can read Location header
    });

    // If backend responded with a redirect status, Location header contains IdP url
    if (res.status === 302 || res.status === 303 || res.status === 307 || res.status === 308) {
      const location = res.headers.get("Location");
      if (location) return { authorizationUrl: location };
    }

    // If backend returned JSON, parse and return it
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        return await res.json(); // expected { authorizationUrl, state }
      } catch (e) {
        // fallthrough to error below
      }
    }

    // If we get HTML (index.html) or unexpected content, surface clear error
    const text = await res.text();
    throw new Error(`Unexpected response from /auth/login: ${res.status} ${text.slice(0, 200)}`);
  }

  async getCurrentUser() {
    const res = await fetch(`${this.baseURL}/auth/me`, {
      method: "GET",
      credentials: "include",
      headers: { ...this.headers },
    });
    if (!res.ok) return null;
    return await res.json();
  }

  async refreshToken() {
    const res = await fetch(`${this.baseURL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...this.headers },
      body: JSON.stringify({}), // backend can use cookie; include body if configured
    });
    if (!res.ok) throw new Error("Refresh failed");
    return await res.json();
  }

  async logout(payload = {}) {
    try {
      const res = await fetch(`${this.baseURL}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...this.headers },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        console.warn("Logout endpoint returned non-200 status, clearing local session anyway");
      }
      return await res.json().catch(() => ({ logoutUrl: "/" }));
    } catch (error) {
      console.warn("Logout fetch failed, clearing local session:", error);
      // Clear local session even if backend logout fails
      return { logoutUrl: "/" };
    }
  }

  async giveConsent() {
    const res = await fetch(`${this.baseURL}/auth/consent`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...this.headers },
    });
    if (!res.ok) throw new Error("Consent failed");
    return await res.json();
  }
}


// Export service instances
export const questionService = new QuestionService();
export const submissionService = new SubmissionService();
export const userService = new UserService();
export const llmService = new LLMService();
export const courseService = new CourseService();
export const analyticsService = new AnalyticsService();
export const errorsService = new ErrorsService();
export const hintService = new HintService();
export const knowledgeTracingService = new KnowledgeTracingService();
export const problemSetService = new ProblemSetService();
export const authService = new AuthService();

export default ApiService;